import { useCallback, useEffect, useRef, useState } from 'react';
import { useToastStore } from '../../stores/toast-store';
import './Toasts.scss';

// Matches the @toastAnimation :leave duration in Toasts.scss.
const LEAVE_ANIMATION_MS = 150;

// Port of src/app/components/toast/ (mypart-toast).
//
// Timing mechanism replicated exactly from the Angular component:
// - For every new toast a setTimeout is registered for `expiresAt - now`
//   (toasts already expired are removed immediately) — Angular's manageToasts(),
//   run from an effect() on the toasts signal → here an effect on the store
//   selector.
// - Timeouts of toasts that no longer exist in the store are cleared.
// - A 100ms safety interval additionally removes any toast whose `expiresAt`
//   has passed — Angular's checkExpiredToasts() started in ngOnInit.
// - removeToast clears the toast's timeout and removes it from the store.
// - All timeouts and the interval are cleared on unmount (ngOnDestroy).
//
// Animation: Angular's @toastAnimation :enter and :leave are both ported as CSS
// classes (.toast-animate-enter / .toast-animate-leave, see Toasts.scss). To
// replicate Angular's :leave tween, a dismissed toast is first marked as
// "leaving" (plays the 150ms exit animation) and only removed from the store
// after that duration — mirroring how Angular keeps the element in the DOM for
// the leave transition.

export default function Toasts() {
  const toasts = useToastStore((s) => s.toasts);
  const toastTimeoutsRef = useRef<Map<string, number>>(new Map());
  const leaveTimeoutsRef = useRef<Map<string, number>>(new Map());
  const [leavingIds, setLeavingIds] = useState<Set<string>>(new Set());

  const removeToast = useCallback((id: string) => {
    // Clear the auto-dismiss timeout for this toast
    const toastTimeouts = toastTimeoutsRef.current;
    if (toastTimeouts.has(id)) {
      clearTimeout(toastTimeouts.get(id));
      toastTimeouts.delete(id);
    }

    // Already animating out — don't double-trigger.
    if (leaveTimeoutsRef.current.has(id)) {
      return;
    }

    // Play the :leave animation, then remove from the store once it finishes.
    setLeavingIds(prev => new Set(prev).add(id));
    const leaveTimeout = window.setTimeout(() => {
      leaveTimeoutsRef.current.delete(id);
      setLeavingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      useToastStore.getState().removeToast(id);
    }, LEAVE_ANIMATION_MS);
    leaveTimeoutsRef.current.set(id, leaveTimeout);
  }, []);

  // Angular: effect(() => { this.toasts = toastsSignal(); this.manageToasts(); })
  useEffect(() => {
    const toastTimeouts = toastTimeoutsRef.current;

    // Set up timeouts for any new toasts
    toasts.forEach(toast => {
      if (!toastTimeouts.has(toast.id)) {
        const timeRemaining = toast.expiresAt - Date.now();
        if (timeRemaining > 0) {
          const timeoutId = window.setTimeout(() => {
            removeToast(toast.id);
          }, timeRemaining);
          toastTimeouts.set(toast.id, timeoutId);
        } else {
          // Toast already expired
          removeToast(toast.id);
        }
      }
    });

    // Clean up timeouts for toasts that no longer exist
    for (const [id, timeout] of toastTimeouts.entries()) {
      if (!toasts.some(toast => toast.id === id)) {
        clearTimeout(timeout);
        toastTimeouts.delete(id);
      }
    }
  }, [toasts, removeToast]);

  // Angular ngOnInit: check for expired toasts every 100ms; ngOnDestroy: clear all.
  useEffect(() => {
    const checkInterval = window.setInterval(() => {
      const now = Date.now();
      const expiredToastIds = useToastStore.getState().toasts
        .filter(toast => toast.expiresAt <= now)
        .map(toast => toast.id);

      if (expiredToastIds.length > 0) {
        expiredToastIds.forEach(id => removeToast(id));
      }
    }, 100);

    const toastTimeouts = toastTimeoutsRef.current;
    const leaveTimeouts = leaveTimeoutsRef.current;
    return () => {
      clearInterval(checkInterval);
      for (const timeoutId of toastTimeouts.values()) {
        clearTimeout(timeoutId);
      }
      toastTimeouts.clear();
      for (const timeoutId of leaveTimeouts.values()) {
        clearTimeout(timeoutId);
      }
      leaveTimeouts.clear();
    };
  }, [removeToast]);

  return (
    <div className="fixed top-0 left-1/2 transform -translate-x-1/2 m-4 z-[15000] toast-container flex flex-col gap-2">
      {toasts.map(toast => (
        <div key={toast.id}
             className={`toast ${leavingIds.has(toast.id) ? 'toast-animate-leave' : 'toast-animate-enter'} ${toast.type}`}>
          <div className="toast-icon">
            <div className={`icon ${toast.type}`}></div>
          </div>
          <div className="toast-content">
            {/* Angular: [innerHTML]="toast.message" — messages are app-internal
                strings (e.g. environment.toastSupportMessage with an <a> tag),
                never user input. */}
            <p dangerouslySetInnerHTML={{ __html: toast.message }}></p>
          </div>
          <button
              onClick={() => removeToast(toast.id)}
              aria-label="Close notification"
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900
           rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white toast-close"
              type="button">
            <svg aria-hidden="true" className="w-3 h-3 close-icon" fill="none" viewBox="0 0 14 14"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" stroke="currentColor" strokeLinecap="round"
                    strokeLinejoin="round" strokeWidth="2"/>
            </svg>
            <span className="sr-only">Close modal</span>
          </button>
        </div>
      ))}
    </div>
  );
}
